import { fetchBorrowerVerifications } from "../../../../thunks/AnalysisStep";
import { StakeholderSubType, VerificationDetails, VerificationGroupType } from "../../../../api/AnalysisStep";
import { createEntityAdapter, createSelector, createSlice, EntityState } from "@reduxjs/toolkit";
import { addVerifications, updateVerification } from "../../../../thunks/Verifications";
import { RootState } from "../../../../app/store";

interface LoanState {
    loanReferenceId: string;
    borrowerIds?: string[];
}

export const LOAN_DEFAULT_BORROWER_SUFFIX = "-default-borrower";

interface BorrowersState {
    borrowerId: string
    borrower_name?: string;
    borrower_type?: StakeholderSubType;
    identityVerificationIds?: string[];
    profileVerificationIds?: string[];
}

const borrowersEntityAdapter = createEntityAdapter<BorrowersState, string>({
    selectId: (borrower) => borrower.borrowerId
})

const borrowerSelectors = borrowersEntityAdapter.getSelectors();

const loansEntityAdapter = createEntityAdapter<LoanState, string>({
    selectId: (loan) => loan.loanReferenceId
})

const loanSelectors = loansEntityAdapter.getSelectors();

const verificationEntityAdapter = createEntityAdapter<VerificationDetails, string>({
    selectId: (verification) => verification.document_id
})

const verificationSelectors = verificationEntityAdapter.getSelectors();

interface VerificationsState {
    loans: EntityState<LoanState, string>;
    borrowers: EntityState<BorrowersState, string>;
    verifications: EntityState<VerificationDetails, string>;
    identityProfileLoadingState: 'idle' | 'loading' | 'error'
}

const initialState: VerificationsState = {
    loans: loansEntityAdapter.getInitialState(),
    borrowers: borrowersEntityAdapter.getInitialState(),
    verifications: verificationEntityAdapter.getInitialState(),
    identityProfileLoadingState: 'idle',
}

const verificationSlice = createSlice({
  name: "verifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchBorrowerVerifications.pending, (state) => {
        state.identityProfileLoadingState = 'loading';
        loansEntityAdapter.removeAll(state.loans);
        borrowersEntityAdapter.removeAll(state.borrowers);
        verificationEntityAdapter.removeAll(state.verifications);
    })
    builder.addCase(fetchBorrowerVerifications.rejected, (state) => {
        state.identityProfileLoadingState = 'error';
    })

    builder.addCase(fetchBorrowerVerifications.fulfilled, (state, action) => {
        const response = action.payload;
        const loanReferenceIds = action.meta.arg.loan_reference_ids;
        const loanReferenceIdsFound: string[] = [];
        
        response.loan_verifications.forEach((loanVerification) => {
            loanReferenceIdsFound.push(loanVerification.loan_reference_id)
            const borrowerIds: string[] = []

            loanVerification.borrower_verifications.forEach((borrowerVerification) => {
                borrowerIds.push(borrowerVerification.borrower_id);
                
                const identityVerificationIds: string[] = [];
                const profileVerificationIds: string[] = [];
                
                borrowerVerification.verification_groups.forEach((verificationGroup) => {
                    verificationGroup.verifications.forEach((verificationDetails) => {
                        if(verificationGroup.group_name===VerificationGroupType.BORROWER_IDENTITY){
                            identityVerificationIds.push(verificationDetails.document_id);
                        }
                        if(verificationGroup.group_name===VerificationGroupType.BORROWER_PROFILE){
                            profileVerificationIds.push(verificationDetails.document_id)
                        }
                        verificationEntityAdapter.upsertOne(state.verifications, verificationDetails)
                    })
                })

                borrowersEntityAdapter.upsertOne(state.borrowers, {
                    borrowerId: borrowerVerification.borrower_id,
                    borrower_name: borrowerVerification.borrower_name,
                    borrower_type: borrowerVerification.borrower_type,
                    identityVerificationIds: identityVerificationIds,
                    profileVerificationIds: profileVerificationIds
                })
            })

            loansEntityAdapter.upsertOne(state.loans, {
                loanReferenceId: loanVerification.loan_reference_id,
                borrowerIds: borrowerIds
            })
        })

        const loanReferenceIdsMissing: string[] = loanReferenceIds?.filter(loanReferenceId => !loanReferenceIdsFound.includes(loanReferenceId)) || [];

        loanReferenceIdsMissing.forEach((lrId) => {
            borrowersEntityAdapter.upsertOne(state.borrowers, {
                borrowerId: lrId+LOAN_DEFAULT_BORROWER_SUFFIX,
                identityVerificationIds: [],
                profileVerificationIds: []
            })

            loansEntityAdapter.upsertOne(state.loans, {
                loanReferenceId: lrId,
                borrowerIds: [lrId+LOAN_DEFAULT_BORROWER_SUFFIX]
            })
        })

        state.identityProfileLoadingState = 'idle';
    })

    builder.addCase(updateVerification.fulfilled, (state, action) => {
        const document_id = action.meta.arg.id;
        verificationEntityAdapter.updateOne(state.verifications, {
            id: document_id,
            changes: action.meta.arg.request
        })
    })

    builder.addCase(addVerifications.fulfilled, (state, action) => {
        const verifications = action.meta.arg.request.verifications;

        verifications.forEach((verification) => {

            verificationEntityAdapter.addOne(state.verifications, {
                document_type: verification.document_type,
                document_id: verification.document_id,
                onboarding_details: verification.onboarding_details || null,
                current_details: verification.current_details,
                evidence_link: verification.evidence_link,
            })
            
            if(verification.group===VerificationGroupType.BORROWER_IDENTITY && verification.customer_id){
                const borrower = borrowerSelectors.selectById(state.borrowers, verification.customer_id);
                const identityVerificationIds = borrower.identityVerificationIds || [];
                borrowersEntityAdapter.upsertOne(state.borrowers, {
                    borrowerId: verification.customer_id,
                    identityVerificationIds: [...identityVerificationIds, verification.document_id]
                })
            }
            if(verification.group===VerificationGroupType.BORROWER_PROFILE && verification.customer_id){
                const borrower = borrowerSelectors.selectById(state.borrowers, verification.customer_id);
                const profileVerificationIds = borrower.profileVerificationIds || [];
                borrowersEntityAdapter.upsertOne(state.borrowers, {
                    borrowerId: verification.customer_id,
                    profileVerificationIds: [...profileVerificationIds, verification.document_id]
                })
            }
        })
    })
  }
})

export const verificationsReducer = verificationSlice.reducer;

export const selectVerificationState = (state: RootState) => state.verificationsData;

export const selectLoans = createSelector(
    [selectVerificationState],
    (verificationsData) => verificationsData.loans
)

export const selectBorrowers = createSelector(
    [selectVerificationState],
    (verificationsData) => verificationsData.borrowers
)

export const selectVerifications = createSelector(
    [selectVerificationState],
    (verificationsData) => verificationsData.verifications
)

export const selectLoansAndBorrowers = createSelector(
    [selectLoans, selectBorrowers],
    (loanState, borrowersState) => {
        const loans = loanSelectors.selectAll(loanState);
        
        const result = loans.map((loan) => {
            const loanBorrowers = loan.borrowerIds?.map(
                id => borrowerSelectors.selectById(borrowersState, id)
            ).map(borrower => ({
                borrower_id: borrower.borrowerId,
                name: borrower.borrower_name,
                is_primary: borrower.borrower_type === StakeholderSubType.PRIMARY_BORROWER
            }))

            return {
                loanReferenceId: loan.loanReferenceId,
                borrowers: loanBorrowers
            }
        })

        return result;
    }
)

export const selectIdentityProfileVerificationState = (state: RootState) => {
    return state.verificationsData.identityProfileLoadingState;
}

export const selectIdentityVerificationsByBorrower = createSelector(
    [selectBorrowers, selectVerifications, (_:RootState, borrowerId?: string) => borrowerId],

    (borrowers, verifications, borrowerId) => {
        
        if(!borrowerId)
            return [];

        const identityVerificationIds = borrowerSelectors.selectById(borrowers, borrowerId).identityVerificationIds;

        const result = identityVerificationIds?.map(id => verificationSelectors.selectById(verifications, id)) || [];

        return result;
    }
)

export const selectProfileVerificationsByBorrower = createSelector(
    [selectBorrowers, selectVerifications, (_:RootState, borrowerId?: string) => borrowerId],
    (borrowers, verifications, borrowerId) => {

        if(!borrowerId)
            return [];

        const profileVerificationIds = borrowerSelectors.selectById(borrowers, borrowerId).profileVerificationIds;

        const result = profileVerificationIds?.map(id => verificationSelectors.selectById(verifications, id)) || [];

        return result;
    }
)
